import React, { useEffect } from "react";
import Navbar from "../components/layout/Navbar";
import Reveal from "../components/common/Reveal";
import Footer from "../components/layout/Footer";

const PrivacyPage: React.FC = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return (
    <div className="min-h-screen bg-primary">
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary via-accentGold/5 to-accentNavy/10 py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-accentNavy/20 via-transparent to-accentGold/15" />
        <div className="absolute -right-32 top-1/2 h-96 w-96 rounded-full bg-accentGold/30 blur-3xl" />
        <div className="absolute -left-32 bottom-1/4 h-96 w-96 rounded-full bg-accentNavy/20 blur-3xl" />

        <div className="relative z-10 mx-auto max-w-4xl px-6 lg:px-10">
          <Reveal>
            <a
              href="#home"
              className="mb-8 inline-flex items-center gap-2 text-sm font-semibold text-accentNavy/70 transition-colors duration-200 hover:text-accentGold"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Back to Home
            </a>
          </Reveal>

          <Reveal delay={120}>
            <h1 className="mb-4 font-display text-4xl leading-tight text-accentNavy sm:text-5xl lg:text-6xl">
              Privacy Policy
            </h1>
            <p className="text-lg text-accentNavy/70">
              Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Content Section */}
      <section className="relative bg-gradient-to-b from-primary via-primary/95 to-primary/90 py-16">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <div className="space-y-8 text-accentNavy/80 leading-relaxed">
            <Reveal>
              <div className="rounded-2xl border border-white/50 bg-white/90 p-8 shadow-[0_10px_30px_-15px_rgba(9,32,63,0.2)]">
                <h2 className="mb-4 text-2xl font-semibold text-accentNavy">1. Information We Collect</h2>
                <p className="mb-4 text-base leading-relaxed">
                  We collect information that you provide directly to us, including:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base">
                  <li>Name and contact information (email address, phone number)</li>
                  <li>Payment information (processed securely through Razorpay)</li>
                  <li>Course enrollment and attendance information</li>
                  <li>Communication preferences and feedback</li>
                </ul>
              </div>
            </Reveal>

            <Reveal delay={120}>
              <div className="rounded-2xl border border-white/50 bg-white/90 p-8 shadow-[0_10px_30px_-15px_rgba(9,32,63,0.2)]">
                <h2 className="mb-4 text-2xl font-semibold text-accentNavy">2. How We Use Your Information</h2>
                <p className="mb-4 text-base leading-relaxed">
                  We use the information we collect to:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base">
                  <li>Process your course enrollments and payments</li>
                  <li>Communicate with you about your courses and our services</li>
                  <li>Send you important updates and notifications</li>
                  <li>Improve our services and user experience</li>
                  <li>Comply with legal obligations</li>
                </ul>
              </div>
            </Reveal>

            <Reveal delay={180}>
              <div className="rounded-2xl border border-white/50 bg-white/90 p-8 shadow-[0_10px_30px_-15px_rgba(9,32,63,0.2)]">
                <h2 className="mb-4 text-2xl font-semibold text-accentNavy">3. Payment Processing</h2>
                <p className="text-base leading-relaxed">
                  All payment transactions are processed securely through Razorpay. We do not store your complete payment card details on our servers. Razorpay handles all payment data in accordance with their privacy policy and security standards. For more information, please review{" "}
                  <a
                    href="https://razorpay.com/privacy/"
                    target="_blank"
                    rel="noreferrer"
                    className="text-accentGold hover:underline font-medium"
                  >
                    Razorpay's Privacy Policy
                  </a>
                  .
                </p>
              </div>
            </Reveal>

            <Reveal delay={240}>
              <div className="rounded-2xl border border-white/50 bg-white/90 p-8 shadow-[0_10px_30px_-15px_rgba(9,32,63,0.2)]">
                <h2 className="mb-4 text-2xl font-semibold text-accentNavy">4. Data Security</h2>
                <p className="text-base leading-relaxed">
                  We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet or electronic storage is 100% secure.
                </p>
              </div>
            </Reveal>

            <Reveal delay={300}>
              <div className="rounded-2xl border border-white/50 bg-white/90 p-8 shadow-[0_10px_30px_-15px_rgba(9,32,63,0.2)]">
                <h2 className="mb-4 text-2xl font-semibold text-accentNavy">5. Information Sharing</h2>
                <p className="mb-4 text-base leading-relaxed">
                  We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base">
                  <li>With service providers who assist us in operating our website and conducting our business (e.g., payment processors)</li>
                  <li>When required by law or to protect our rights</li>
                  <li>With your explicit consent</li>
                </ul>
              </div>
            </Reveal>

            <Reveal delay={360}>
              <div className="rounded-2xl border border-white/50 bg-white/90 p-8 shadow-[0_10px_30px_-15px_rgba(9,32,63,0.2)]">
                <h2 className="mb-4 text-2xl font-semibold text-accentNavy">6. Cookies and Tracking</h2>
                <p className="text-base leading-relaxed">
                  We use cookies and similar tracking technologies to enhance your experience on our website, analyze usage patterns, and improve our services. You can control cookie preferences through your browser settings.
                </p>
              </div>
            </Reveal>

            <Reveal delay={420}>
              <div className="rounded-2xl border border-white/50 bg-white/90 p-8 shadow-[0_10px_30px_-15px_rgba(9,32,63,0.2)]">
                <h2 className="mb-4 text-2xl font-semibold text-accentNavy">7. Your Rights</h2>
                <p className="mb-4 text-base leading-relaxed">
                  You have the right to:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base">
                  <li>Access and receive a copy of your personal data</li>
                  <li>Request correction of inaccurate data</li>
                  <li>Request deletion of your personal data</li>
                  <li>Object to processing of your personal data</li>
                  <li>Withdraw consent at any time</li>
                </ul>
              </div>
            </Reveal>

            <Reveal delay={480}>
              <div className="rounded-2xl border border-white/50 bg-white/90 p-8 shadow-[0_10px_30px_-15px_rgba(9,32,63,0.2)]">
                <h2 className="mb-4 text-2xl font-semibold text-accentNavy">8. Changes to This Policy</h2>
                <p className="text-base leading-relaxed">
                  We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
                </p>
              </div>
            </Reveal>

            <Reveal delay={540}>
              <div className="rounded-2xl border border-white/50 bg-white/90 p-8 shadow-[0_10px_30px_-15px_rgba(9,32,63,0.2)]">
                <h2 className="mb-4 text-2xl font-semibold text-accentNavy">9. Contact Us</h2>
                <p className="text-base leading-relaxed">
                  If you have any questions about this Privacy Policy or wish to exercise your rights, please contact us at{" "}
                  <a
                    href="mailto:admin@ukuleleseekho.com"
                    className="text-accentGold hover:underline font-medium"
                  >
                    admin@ukuleleseekho.com
                  </a>
                  .
                </p>
              </div>
            </Reveal>
          </div>

          {/* Back Button */}
          <Reveal delay={600}>
            <div className="mt-12 flex items-center justify-center">
              <a
                href="#home"
                className="inline-flex items-center gap-2 rounded-full border border-accentNavy/20 bg-white/80 px-6 py-3 text-sm font-semibold uppercase tracking-[0.15em] text-accentNavy transition-all duration-300 hover:border-accentGold hover:bg-accentGold hover:text-white"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
                Back to Home
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPage;

